/**
 * Hydration Service - Transforms backend API data to frontend data structures
 *
 * PURPOSE:
 * Converts minimal storage formats (ProjectInfo, ConfigurationInfo, BondSeriesStorable)
 * into fully-hydrated frontend structures (ProjectData, Configuration, BondSeries).
 *
 * KEY TRANSFORMATIONS:
 * - ProjectInfo → ProjectData: Rebuilds dateRecords, dateElements, taxableValueMap, existingDebtProjects
 * - ConfigurationInfo → Configuration: Reconstructs full BondSeries from minimal storage
 * - BondSeriesStorable → BondSeries: Derives payment dates and bondPaymentMap from project dates
 *
 * DEPENDENCIES:
 * - BuildService patterns (reused logic for calculated data)
 * - BondSeriesBuilder (for bondPaymentMap construction)
 * - DateBuilder, TaxableValueBuilder, ExistingDebtBuilder
 */

import type {
  BondSeriesStorable,
  ConfigurationInfo,
  ConfigurationInputs,
  ProjectInfo,
  ProjectInputs,
} from '@shared/models';
import { BondSeriesBuilder } from '~/builders/bond-series/BondSeriesBuilder';
import { ExistingDebtBuilder } from '~/builders/existing-debt/ExistingDebtBuilder';
import { convertToEngineFormat as convertExistingDebt } from '~/builders/existing-debt/ExistingDebtInputs';
import { DateBuilder } from '~/builders/project-dates/DateBuilder';
import { TaxableValueBuilder } from '~/builders/taxable-value/TaxableValueBuilder';
import { convertToEngineFormat as convertTaxValues } from '~/builders/taxable-value/TaxableValueInputs';
import { isBondSeriesComplete } from '~/hooks/useExistingDebtHandlers';
import type { ProjectData, ProjectMetadata } from '~/hooks/useProjectStore';
import { logger } from '~/services/logging';
import type {
  BondSeries,
  Configuration,
  DateInputsForm,
  DateRecord,
  DRFInputsForm,
  ExistingDebtInputForm,
  ExistingDebtProject,
  MillageInputsForm,
  Project,
  ProjectDateElements,
  TaxableValueMap,
  TaxableValueMapEntry,
  TaxValueInputsForm,
} from '~/types';
import type { BondInterestRateSettings } from '~/types/app-settings';
import { DEFAULT_APP_SETTINGS } from '~/types/app-settings';
import type { ConfigurationOverrides, CustomizationMetadata } from '~/types/overrides';
import { sanitizeFormInputsForStorage } from '~/utils/numberParsing';

// ============================================
// Project Hydration
// ============================================

/**
 * Hydrates a ProjectInfo (from API) into a fully-populated ProjectData (for frontend)
 *
 * Rebuilds all calculated data from stored inputs:
 * - dateRecords from dateInputs
 * - dateElements (with maps) from dateRecords
 * - taxableValueMap from taxValueInputs + dateRecords
 * - existingDebtProjects from existingDebtInputs + dateRecords
 */
export function hydrateProject(projectInfo: ProjectInfo): ProjectData {
  const { id, name, clientName, createdAt, updatedAt, inputs } = projectInfo;

  // Build metadata
  const metadata: ProjectMetadata = {
    id,
    name,
    clientName,
    createdAt,
    updatedAt,
    isTestingProject: projectInfo.isTestingProject,
    testScenarioId: projectInfo.testScenarioId,
  };

  // Extract raw form inputs from JSONB (cast through unknown for type safety)
  const dateInputs = (inputs.dateInputs as unknown as DateInputsForm) || null;
  const taxValueInputs = (inputs.taxValueInputs as unknown as TaxValueInputsForm) || null;
  const existingDebtInputs =
    (inputs.existingDebtInputs as unknown as ExistingDebtInputForm[]) || null;
  const millageInputs = (inputs.millageInputs as unknown as MillageInputsForm) || null;
  const drfInputs = (inputs.drfInputs as unknown as DRFInputsForm) || null;
  const bondInterestRateSettings =
    (inputs.bondInterestRateSettings as unknown as BondInterestRateSettings) || null;

  // Build calculated data
  const dateRecords = buildDateRecordsFromInputs(dateInputs);
  const dateElements = buildDateElementsFromRecords(dateRecords, dateInputs, taxValueInputs);
  const taxableValueMap = buildTaxableValueMapFromInputs(taxValueInputs, dateInputs, dateRecords);
  const existingDebtProjects = buildExistingDebtProjectsFromInputs(existingDebtInputs, dateRecords);

  return {
    metadata,
    // Raw form inputs (stored)
    dateInputs,
    taxValueInputs,
    existingDebtInputs,
    millageInputs,
    drfInputs,
    bondInterestRateSettings,
    // Calculated data (rebuilt)
    dateRecords,
    dateElements,
    taxableValueMap,
    existingDebtProjects,
    // Configurations and favorites loaded separately
    configurations: null,
    favorites: null,
  };
}

/**
 * Build dateRecords from dateInputs
 */
function buildDateRecordsFromInputs(dateInputs: DateInputsForm | null): DateRecord[] | null {
  if (!dateInputs) {
    return null;
  }

  try {
    const validation = DateBuilder.validateInputs(dateInputs);
    if (!validation.isValid) {
      logger.L2.debug('[Hydration] DateInputs validation failed', {
        context: 'hydration',
        errors: validation.errors,
      });
      return null;
    }

    const engineInputs = DateBuilder.convertToEngineFormat(dateInputs);
    return DateBuilder.buildDateRecords(engineInputs);
  } catch (error) {
    logger.L2.debug('[Hydration] Error building dateRecords', {
      context: 'hydration',
      error: String(error),
    });
    return null;
  }
}

/**
 * Build dateElements (with maps) from dateRecords
 */
function buildDateElementsFromRecords(
  dateRecords: DateRecord[] | null,
  dateInputs: DateInputsForm | null,
  taxValueInputs: TaxValueInputsForm | null
): ProjectDateElements | null {
  if (!dateRecords || dateRecords.length === 0) {
    return null;
  }

  try {
    // Create a minimal Project object for buildBaseProjectElements
    const projectForMaps: Project = {
      id: 'hydration-temp',
      name: 'Hydration Temp',
      dateInputs,
      taxValueInputs,
      existingDebtInputs: null,
      millageInputs: null,
      drfInputs: null,
      taxableValueMap: null,
      existingDebtProjects: null,
      dateElements: {
        dateRecords,
        dateRecordsMapByTaxYear: undefined,
        dateRecordsMapByPaymentDate: undefined,
      },
      configurations: null,
      lastUpdated: new Date().toISOString(),
      processingStatus: 'complete',
    };

    return DateBuilder.buildBaseProjectElements(projectForMaps);
  } catch (error) {
    logger.L2.debug('[Hydration] Error building dateElements', {
      context: 'hydration',
      error: String(error),
    });
    return null;
  }
}

/**
 * Build taxableValueMap from taxValueInputs + dateRecords
 */
function buildTaxableValueMapFromInputs(
  taxValueInputs: TaxValueInputsForm | null,
  dateInputs: DateInputsForm | null,
  dateRecords: DateRecord[] | null
): TaxableValueMap | null {
  if (!taxValueInputs || !dateRecords || dateRecords.length === 0 || !dateInputs) {
    return null;
  }

  try {
    const engineInputs = convertTaxValues(taxValueInputs);
    const validation = TaxableValueBuilder.validateInputs(engineInputs);
    if (!validation.isValid) {
      logger.L2.debug('[Hydration] TaxValueInputs validation failed', {
        context: 'hydration',
        errors: validation.errors,
      });
      return null;
    }

    const startingTaxYear = parseInt(dateInputs.startingTaxYear || '', 10);
    const years = dateRecords.length;

    const taxableValueArray = TaxableValueBuilder.buildWithFixedYears(
      engineInputs,
      startingTaxYear,
      years
    );

    // Convert array to map keyed by tax year
    const result = taxableValueArray.reduce(
      (map, entry) => {
        map[entry.taxYear] = entry;
        return map;
      },
      {} as Record<number, TaxableValueMapEntry>
    );

    return result;
  } catch (error) {
    logger.L2.debug('[Hydration] Error building taxableValueMap', {
      context: 'hydration',
      error: String(error),
    });
    return null;
  }
}

/**
 * Build existingDebtProjects from existingDebtInputs + dateRecords
 */
function buildExistingDebtProjectsFromInputs(
  existingDebtInputs: ExistingDebtInputForm[] | null,
  dateRecords: DateRecord[] | null
): ExistingDebtProject[] | null {
  if (!existingDebtInputs || existingDebtInputs.length === 0 || !dateRecords) {
    return null;
  }

  try {
    // Filter to complete projects only
    const completeProjects = existingDebtInputs.filter((form) => {
      if (!form.id?.trim() || !form.name?.trim() || !form.payments || form.payments.length === 0) {
        return false;
      }
      return isBondSeriesComplete(form);
    });

    if (completeProjects.length === 0) {
      return null;
    }

    const builtProjects = completeProjects.map((form) => {
      const engineFormat = convertExistingDebt(form);
      const processed = ExistingDebtBuilder.processDebtProject(engineFormat, dateRecords);
      return processed;
    });

    return builtProjects;
  } catch (error) {
    logger.L2.debug('[Hydration] Error building existingDebtProjects', {
      context: 'hydration',
      error: String(error),
    });
    return null;
  }
}

// ============================================
// Configuration Hydration
// ============================================

/**
 * Hydrates a ConfigurationInfo (from API) into a Configuration (for frontend)
 *
 * Requires a hydrated ProjectData to derive BondSeries payment dates
 */
export function hydrateConfiguration(
  configInfo: ConfigurationInfo,
  project: ProjectData
): Configuration | null {
  const { id, name, inputs } = configInfo;

  // Ensure project has required date elements
  if (!project.dateElements?.dateRecordsMapByPaymentDate || !project.dateRecords) {
    logger.L2.debug('[Hydration] Cannot hydrate configuration - project missing date elements', {
      context: 'hydration',
      configId: id,
    });
    return null;
  }

  try {
    // Reconstruct full BondSeries from minimal storage
    const bondSeries = inputs.bondSeries.map((storable) =>
      reconstructBondSeries(storable, project)
    );

    // Filter out any bonds that failed to reconstruct
    const validBondSeries = bondSeries.filter((bond): bond is BondSeries => bond !== null);

    if (validBondSeries.length === 0) {
      logger.L2.debug('[Hydration] No valid bonds after reconstruction', {
        context: 'hydration',
        configId: id,
      });
      return null;
    }

    return {
      id,
      name: name || undefined,
      orderId: 0, // Will be set by store based on position
      createdAt: configInfo.createdAt, // Preserve creation date for sorting
      bondSeries: validBondSeries,
      millageInputs:
        (inputs.millageInputs as unknown as MillageInputsForm) || project.millageInputs,
      drfInputs: (inputs.drfInputs as unknown as DRFInputsForm) || project.drfInputs,
      // Candidates NOT restored - regenerated on demand (empty array, not undefined)
      candidates: [],
      totalCandidates: 0,
      // Restore override customizations
      overrides: inputs.overrides ? (inputs.overrides as unknown as ConfigurationOverrides) : null,
      customizationMetadata: inputs.customizationMetadata
        ? (inputs.customizationMetadata as unknown as CustomizationMetadata)
        : null,
    };
  } catch (error) {
    logger.L2.debug('[Hydration] Error hydrating configuration', {
      context: 'hydration',
      configId: id,
      error: String(error),
    });
    return null;
  }
}

/**
 * Hydrates multiple configurations and assigns sequential orderIds
 * Configurations are sorted by createdAt (oldest first) before assigning orderIds
 */
export function hydrateConfigurations(
  configInfos: ConfigurationInfo[],
  project: ProjectData
): Configuration[] {
  const hydrated = configInfos
    .map((info) => hydrateConfiguration(info, project))
    .filter((config): config is Configuration => config !== null);

  // Sort by createdAt (oldest first) to ensure consistent ordering
  const sorted = hydrated.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  });

  // Assign sequential orderIds based on createdAt order
  return sorted.map((config, index) => ({
    ...config,
    orderId: index + 1,
  }));
}

// ============================================
// Bond Series Reconstruction
// ============================================

/**
 * Reconstructs a full BondSeries from minimal stored format (BondSeriesStorable)
 *
 * Uses project date records to derive:
 * - interestPaymentMonths, principalPaymentMonths
 * - firstInterestPaymentDate, firstPrincipalPaymentDate, finalPrincipalPaymentDate
 * - __interestRateEscalation (from bond settings or project/app defaults)
 * - bondPaymentMap (via BondSeriesBuilder)
 */
export function reconstructBondSeries(
  storable: BondSeriesStorable,
  project: ProjectData
): BondSeries | null {
  const dateRecords = project.dateRecords;
  if (!dateRecords || dateRecords.length === 0) {
    logger.L2.debug('[Hydration] Cannot reconstruct bond - no date records', {
      context: 'hydration',
      bondId: storable.id,
    });
    return null;
  }

  try {
    // Calculate duration from taxYear if not explicit (storable has taxYear + duration)
    const startingTaxYear = storable.taxYear;
    const duration = storable.duration;
    const finalTaxYear = startingTaxYear + duration - 1;

    // Find date records for starting and final tax years
    const startingYearRecord = dateRecords.find((dr) => dr.taxYear === startingTaxYear);
    const finalYearRecord = dateRecords.find((dr) => dr.taxYear === finalTaxYear);

    if (!startingYearRecord) {
      logger.L2.debug('[Hydration] No date record for starting tax year', {
        context: 'hydration',
        bondId: storable.id,
        taxYear: startingTaxYear,
      });
      return null;
    }

    if (!finalYearRecord) {
      logger.L2.debug('[Hydration] No date record for final tax year', {
        context: 'hydration',
        bondId: storable.id,
        taxYear: finalTaxYear,
      });
      return null;
    }

    // Derive payment months from actual payment dates
    const paymentMonths = new Set<number>();
    startingYearRecord.paymentDates.forEach((dateStr) => {
      const date = new Date(dateStr);
      paymentMonths.add(date.getMonth() + 1);
    });
    const interestPaymentMonths = Array.from(paymentMonths).sort((a, b) => a - b);

    // Principal payment month is the month of the last payment date of fiscal year
    const firstPrincipalPaymentDate = startingYearRecord.lastPaymentDateOfFiscalYear;
    const firstPrincipalDate = new Date(firstPrincipalPaymentDate);
    const principalPaymentMonths = [firstPrincipalDate.getMonth() + 1];

    // First interest payment: First payment date in the starting tax year
    const firstInterestPaymentDate =
      startingYearRecord.paymentDates[0] || firstPrincipalPaymentDate;

    // Final principal payment: Last payment date in final tax year
    const finalPrincipalPaymentDate = finalYearRecord.lastPaymentDateOfFiscalYear;

    // Get interest rate settings (bond → project → app defaults)
    const bondRateSettings = storable.bondInterestRateSettings as
      | BondInterestRateSettings
      | undefined;
    const projectRateSettings = project.bondInterestRateSettings;
    const interestRates =
      bondRateSettings || projectRateSettings || DEFAULT_APP_SETTINGS.bondInterestRates;

    // Build the reconstructed BondSeries (without bondPaymentMap yet)
    const reconstructed: BondSeries & {
      __interestRateEscalation?: {
        startRate: number;
        incrementRate: number;
        maxRate: number;
      };
    } = {
      id: storable.id,
      name: storable.name,
      issueDate: storable.issueDate,
      interestPaymentMonths,
      principalPaymentMonths,
      firstInterestPaymentDate,
      firstPrincipalPaymentDate,
      finalPrincipalPaymentDate,
      bondPaymentMap: {},
      targetInformation: storable.targetInformation as BondSeries['targetInformation'],
      __interestRateEscalation: {
        startRate: interestRates.startRate / 100,
        incrementRate: interestRates.incrementRate / 100,
        maxRate: interestRates.maxRate / 100,
      },
    };

    // Build the bondPaymentMap using BondSeriesBuilder
    if (!project.dateElements?.dateRecordsMapByPaymentDate) {
      logger.L2.debug('[Hydration] Cannot build bondPaymentMap - no dateRecordsMapByPaymentDate', {
        context: 'hydration',
        bondId: storable.id,
      });
      return reconstructed; // Return without bondPaymentMap - can be built later
    }

    const bondPaymentMap = BondSeriesBuilder.buildBondPaymentMap(
      reconstructed,
      project.dateElements.dateRecordsMapByPaymentDate
    );

    return {
      ...reconstructed,
      bondPaymentMap,
    };
  } catch (error) {
    logger.L2.debug('[Hydration] Error reconstructing bond series', {
      context: 'hydration',
      bondId: storable.id,
      error: String(error),
    });
    return null;
  }
}

// ============================================
// Dehydration (Frontend → Backend)
// ============================================

/**
 * Converts ProjectData to ProjectInputs for API storage
 * Strips calculated data, keeps only raw form inputs
 * Sanitizes numeric string fields to ensure raw format (e.g., "57081" not "$57,081.00")
 * Note: Cast to Record<string, unknown> for JSONB storage compatibility
 */
export function dehydrateProjectInputs(project: ProjectData): ProjectInputs {
  // Sanitize all form inputs to strip formatting from numeric fields before storage
  const sanitizedDateInputs = sanitizeFormInputsForStorage(project.dateInputs);
  const sanitizedTaxValueInputs = sanitizeFormInputsForStorage(project.taxValueInputs);
  const sanitizedExistingDebtInputs = sanitizeFormInputsForStorage(project.existingDebtInputs);
  const sanitizedMillageInputs = sanitizeFormInputsForStorage(project.millageInputs);
  const sanitizedDrfInputs = sanitizeFormInputsForStorage(project.drfInputs);

  return {
    dateInputs: (sanitizedDateInputs as unknown as Record<string, unknown>) || undefined,
    taxValueInputs: (sanitizedTaxValueInputs as unknown as Record<string, unknown>) || undefined,
    existingDebtInputs:
      (sanitizedExistingDebtInputs as unknown as Record<string, unknown>[]) || undefined,
    millageInputs: (sanitizedMillageInputs as unknown as Record<string, unknown>) || undefined,
    drfInputs: (sanitizedDrfInputs as unknown as Record<string, unknown>) || undefined,
    bondInterestRateSettings:
      (project.bondInterestRateSettings as unknown as Record<string, unknown>) || undefined,
  };
}

/**
 * Converts BondSeries to BondSeriesStorable for API storage
 * Keeps only minimal fields needed for reconstruction
 * Note: Cast to Record<string, unknown> for JSONB storage compatibility
 *
 * IMPORTANT: Requires project data to correctly determine taxYear from payment date.
 * The calendar year of a payment date does NOT always equal the tax year
 * (e.g., winter collection: May 2026 payment is for tax year 2025).
 */
export function dehydrateBondSeries(
  bond: BondSeries,
  duration: number,
  project: ProjectData
): BondSeriesStorable {
  // Look up the correct tax year from the project's date records
  // This is critical because calendar year != tax year for fiscal year boundaries
  const taxYear = lookupTaxYearFromPaymentDate(bond.firstPrincipalPaymentDate, project);

  // Get bondInterestRateSettings if it exists on the bond
  const bondWithSettings = bond as BondSeries & {
    bondInterestRateSettings?: BondInterestRateSettings;
  };

  return {
    id: bond.id,
    name: bond.name,
    taxYear,
    duration,
    issueDate: bond.issueDate,
    targetInformation: bond.targetInformation as unknown as Record<string, unknown> | undefined,
    bondInterestRateSettings: bondWithSettings.bondInterestRateSettings as unknown as
      | Record<string, unknown>
      | undefined,
  };
}

/**
 * Look up the correct tax year for a payment date using project date records
 *
 * This is necessary because the calendar year of a payment date may not match
 * the tax year due to fiscal year boundaries:
 * - Summer collection: Tax year 2025 → First payment Nov 2025, last payment May 2026
 * - Winter collection: Tax year 2025 → First payment May 2026, last payment Nov 2026
 *
 * Falls back to calendar year if lookup fails (with warning log).
 */
function lookupTaxYearFromPaymentDate(paymentDate: string, project: ProjectData): number {
  const dateRecordsMap = project.dateElements?.dateRecordsMapByPaymentDate;

  if (dateRecordsMap) {
    // Construct the key (YYYYMMDD as number)
    const date = new Date(paymentDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const key = parseInt(`${year}${month}${day}`, 10);

    const dateRecord = dateRecordsMap[key];
    if (dateRecord) {
      return dateRecord.taxYear;
    }
  }

  // Fallback: search through dateRecords array
  if (project.dateRecords) {
    for (const record of project.dateRecords) {
      if (record.lastPaymentDateOfFiscalYear === paymentDate) {
        return record.taxYear;
      }
      if (record.paymentDates.includes(paymentDate)) {
        return record.taxYear;
      }
    }
  }

  // Last resort fallback: use calendar year (not ideal, but better than crashing)
  logger.L2.debug('[Hydration] Could not find tax year for payment date, using calendar year', {
    context: 'hydration',
    paymentDate,
  });
  return new Date(paymentDate).getFullYear();
}

/**
 * Converts Configuration to ConfigurationInputs for API storage
 * Sanitizes numeric string fields to ensure raw format (e.g., "57081" not "$57,081.00")
 * Note: Cast to Record<string, unknown> for JSONB storage compatibility
 *
 * IMPORTANT: Requires project data to correctly determine taxYear for bonds.
 */
export function dehydrateConfigurationInputs(
  config: Configuration,
  project: ProjectData,
  bondDurations?: Map<string, number>
): ConfigurationInputs {
  // Sanitize numeric fields in millageInputs and drfInputs before storage
  const sanitizedMillageInputs = sanitizeFormInputsForStorage(config.millageInputs);
  const sanitizedDrfInputs = sanitizeFormInputsForStorage(config.drfInputs);

  return {
    bondSeries: config.bondSeries.map((bond) => {
      const duration = bondDurations?.get(bond.id) || calculateBondDuration(bond);
      return dehydrateBondSeries(bond, duration, project);
    }),
    millageInputs: (sanitizedMillageInputs as unknown as Record<string, unknown>) || undefined,
    drfInputs: (sanitizedDrfInputs as unknown as Record<string, unknown>) || undefined,
    // Persist override customizations
    overrides: config.overrides ? (config.overrides as unknown as Record<string, unknown>) : null,
    customizationMetadata: config.customizationMetadata
      ? (config.customizationMetadata as unknown as Record<string, unknown>)
      : null,
  };
}

/**
 * Calculate bond duration from payment dates
 */
function calculateBondDuration(bond: BondSeries): number {
  const firstDate = new Date(bond.firstPrincipalPaymentDate);
  const finalDate = new Date(bond.finalPrincipalPaymentDate);
  return finalDate.getFullYear() - firstDate.getFullYear() + 1;
}
